import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SpotifyNowPlayingComponent } from '../spotify-now-playing/spotify-now-playing.component';
import { TranslateService } from '../../../core/services/translate.service';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [SpotifyNowPlayingComponent, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <footer class="border-t border-main py-12">
      <div class="mx-auto max-w-6xl px-6 text-center">
        <!-- sdd-bypass: T4.2 — fixed pixel reservation prevents CLS when Spotify card hydrates (R7) -->
        <div class="flex min-h-[148px] justify-center w-full">
          @defer (on viewport) {
            <app-spotify-now-playing />
          } @placeholder {
            <div
              class="mx-auto flex w-full max-w-sm flex-col gap-2 rounded-2xl border border-main bg-surface-strong px-5 py-4 animate-pulse"
            >
              <div class="flex items-center gap-2">
                <div class="h-3.5 w-3.5 rounded-full bg-main/10"></div>
                <div class="h-3 w-20 rounded bg-main/10"></div>
              </div>
              <div class="flex items-center gap-4">
                <div class="h-14 w-14 rounded-lg bg-main/10"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-4 w-3/4 rounded bg-main/10"></div>
                  <div class="h-3 w-1/2 rounded bg-main/10"></div>
                </div>
              </div>
            </div>
          }
        </div>

        <!-- Social links -->
        <div class="mt-8 flex items-center justify-center gap-4">
          <a
            href="https://www.linkedin.com/in/adrianjimenezcabello"
            target="_blank"
            rel="noopener noreferrer"
            class="flex h-9 w-9 items-center justify-center rounded-lg text-dimmed transition-colors hover:text-[var(--color-info)]"
            aria-label="LinkedIn"
          >
            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
              />
            </svg>
          </a>
          <a
            href="https://github.com/adrijimcab"
            target="_blank"
            rel="noopener noreferrer"
            class="flex h-9 w-9 items-center justify-center rounded-lg text-dimmed transition-colors hover:text-main"
            aria-label="GitHub"
          >
            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"
              />
            </svg>
          </a>
          <a
            href="mailto:adrianjimenezcabello&#64;gmail.com"
            class="flex h-9 w-9 items-center justify-center rounded-lg text-dimmed transition-colors hover:text-main"
            aria-label="Email"
          >
            <svg
              class="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </a>
        </div>

        <!-- Security badge -->
        <div class="mt-6 flex items-center justify-center gap-2 text-xs text-dimmed">
          <svg
            class="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          <span>{{ t.t('footer.security_badge') }}</span>
        </div>

        <p class="mt-4 text-xs text-dimmed">
          {{ t.t('footer.built_with') }}
        </p>
        <p class="mt-4 text-sm text-dimmed">
          &copy; {{ currentYear }} Adrián Jiménez Cabello ·
          <a
            [routerLink]="['/', t.currentLang(), 'privacy']"
            class="hover:text-subtle transition-colors"
            >{{ t.t('footer.privacy_policy') }}</a
          >
        </p>
      </div>
    </footer>
  `,
})
export class FooterComponent {
  protected readonly t = inject(TranslateService);
  readonly currentYear = new Date().getFullYear();
}
